import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const FooterWrapper = styled.footer`
  background: #333 !important;
  color: white;
  padding: 60px 0 30px;
  width: 100%;

  @media (max-width: 768px) {
    padding: 40px 0 20px;
  }
`;

const FooterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  background: transparent !important;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 30px;
    margin-bottom: 30px;
  }
`;

const FooterColumn = styled.div`
  padding-left: 0px;
`;

const ColumnTitle = styled.h3`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 20px;
  color: white;

  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 15px;
  }
`;

const FooterList = styled.ul`
  list-style: none;
  text-align: left;
  padding-left: 0px !important;
`;

const FooterListItem = styled.li`
  margin-bottom: 10px;

  @media (max-width: 768px) {
    margin-bottom: 8px;
  }
`;

const FooterLink = styled(Link)`
  color: #ccc;
  font-size: 14px;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #ff6720;
  }

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ExternalLink = styled.a`
  color: #ccc;
  font-size: 14px;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #ff6720;
  }

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const CompanyInfo = styled.div`
  margin-bottom: 20px;
`;

const CompanyDescription = styled.p`
  font-size: 14px;
  color: #ccc;
  line-height: 1.6;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ContactInfo = styled.div`
  font-size: 14px;
  color: #ccc;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #555;
  border-radius: 50%;
  color: #ccc;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: #ff6720;
    color: white;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid #555;
  padding-top: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 768px) {
    padding-top: 20px;
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
`;

const Copyright = styled.p`
  font-size: 13px;
  color: #999;

  @media (max-width: 768px) {
    order: 2;
  }
`;

const BottomLinks = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    order: 1;
    justify-content: center;
    gap: 15px;
  }
`;

const BottomLink = styled(Link)`
  font-size: 13px;
  color: #ccc;
  text-decoration: none;

  &:hover {
    color: #ff6720;
  }
`;

const Footer = () => {
  return (
    <FooterWrapper>
      <FooterContainer>
        <FooterContent>
          <FooterColumn>
            <ColumnTitle>Products</ColumnTitle>
            <FooterList>
              <FooterListItem>
                <FooterLink to="/collections/outdoor">Outdoor Tools</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/collections/power-tools">
                  Power Tools
                </FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/collections/hand-tools">Hand Tools</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/collections/vacuums">Vacuums</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/collections/combo-kits">Combo Kits</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/collections/accessories">
                  Accessories
                </FooterLink>
              </FooterListItem>
            </FooterList>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Support</ColumnTitle>
            <FooterList>
              <FooterListItem>
                <ExternalLink
                  href="https://support.xplod-group.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Customer Service
                </ExternalLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/warranty">Warranty</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/manuals">Product Manuals</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/parts">Replacement Parts</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/repair">Repair Services</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/contact">Contact Us</FooterLink>
              </FooterListItem>
            </FooterList>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Company</ColumnTitle>
            <FooterList>
              <FooterListItem>
                <FooterLink to="/about">About XPLOD-GROUP</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/careers">Careers</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/press">Press Releases</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/investors">Investor Relations</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <FooterLink to="/sustainability">Sustainability</FooterLink>
              </FooterListItem>
              <FooterListItem>
                <ExternalLink
                  href="https://www.xplod-group.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  XPLOD-GROUPS Corporation
                </ExternalLink>
              </FooterListItem>
            </FooterList>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Connect With Us</ColumnTitle>
            <CompanyInfo>
              <CompanyDescription>
                Get the latest news, product updates, and exclusive offers from
                XPLOD-GROUPS.
              </CompanyDescription>
              <ContactInfo>
                <div>Customer Service: 1-800-544-6986</div>
                <div>Monday - Friday, 8AM - 6PM EST</div>
              </ContactInfo>
              <SocialLinks>
                <SocialLink
                  href="https://www.facebook.com/XplodGroup"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </SocialLink>
                <SocialLink
                  href="https://www.instagram.com/xplod_group/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.585-3.230-1.500.782.915 1.933 1.500 3.230 1.500z" />
                  </svg>
                </SocialLink>
                <SocialLink
                  href="https://www.youtube.com/user/XplodGroup"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </SocialLink>
                <SocialLink
                  href="https://www.pinterest.com/xplodgroup/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Pinterest"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001.012.017.001z" />
                  </svg>
                </SocialLink>
              </SocialLinks>
            </CompanyInfo>
          </FooterColumn>
        </FooterContent>

        <FooterBottom>
          <Copyright>© 2025 AK Designs, Inc. All rights reserved.</Copyright>
          <BottomLinks>
            <BottomLink to="/privacy-policy">Privacy Policy</BottomLink>
            <BottomLink to="/terms-of-service">Terms of Service</BottomLink>
            <BottomLink to="/accessibility-statement">Accessibility</BottomLink>
            <BottomLink to="/cookies">Cookie Settings</BottomLink>
          </BottomLinks>
        </FooterBottom>
      </FooterContainer>
    </FooterWrapper>
  );
};

export default Footer;
